/**
 * Classroom assignments API – all endpoints used by classroom assignments management.
 * Base path: /admin/classroom (classroom assignments routes mounted in backend organization routes).
 *
 * All endpoints return the standard shape:
 * - Success: { success: true, statusCode, message, data }
 * - Error: thrown with message from backend (response: { success: false, statusCode, message, error: { message } })
 * Use response.data for payload (e.g. response.data.assignments, response.data.questions, response.data.submissions).
 */
import {
  DELETE,
  GET,
  POST,
  PUT,
  UPLOAD_FILE,
  UPLOAD_FILE_WITH_METHOD,
} from "../../apiController";

const CLASSROOM_ASSIGNMENTS = '/admin/classroom';

// ==================== CLASSROOM ASSIGNMENTS ====================

// Get assignments for classroom
export const getAssignmentsForClassroom = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/get_assignments`, data);
};

// Create classroom assignment
export const addClassroomAssignment = (data) => {
  // If data is FormData (contains file), use UploadFormData, otherwise use regular POST
  if (data instanceof FormData) {
    return UPLOAD_FILE(`${CLASSROOM_ASSIGNMENTS}/create_assignment`, data);
  } else {
    return POST(`${CLASSROOM_ASSIGNMENTS}/create_assignment`, data);
  }
};

// Delete classroom assignment
export const deleteClassroomAssignment = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/delete_assignment`, data);
};

// Update classroom assignment
export const updateClassroomAssignment = (data) => {
  // If data is FormData (contains file), use UploadFormData with PUT, otherwise use regular PUT
  if (data instanceof FormData) {
    return UPLOAD_FILE(`${CLASSROOM_ASSIGNMENTS}/update_assignment`, data, "PUT");
  } else {
    return PUT(`${CLASSROOM_ASSIGNMENTS}/update_assignment`, data);
  }
};

// ==================== ASSIGNMENT QUESTIONS ====================

// Get assignment questions
export const getClassroomAssignmentQuestions = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/get_assignment_questions`, data);
};

// Add assignment question
export const addClassroomAssignmentQuestion = (data) => {
  // Check if data is FormData and if response_type is text
  if (data instanceof FormData) {
    // Get response_type from FormData
    const questionType = data.get("question_type");

    if (questionType === "text") {
      return POST(`${CLASSROOM_ASSIGNMENTS}/add_assignment_question`, data);
    } else {
      return UPLOAD_FILE(`${CLASSROOM_ASSIGNMENTS}/add_assignment_question`, data);
    }
  } else {
    return POST(`${CLASSROOM_ASSIGNMENTS}/add_assignment_question`, data);
  }
};

// Update assignment question
export const updateClassroomAssignmentQuestion = (data) => {
  if (data instanceof FormData) {
    // Get response_type from FormData
    const questionType = data.get("question_type");

    if (questionType === "text") {
      return POST(`${CLASSROOM_ASSIGNMENTS}/update_assignment_question`, data);
    } else {
      return UPLOAD_FILE(
        `${CLASSROOM_ASSIGNMENTS}/update_assignment_question`,
        data
      );
    }
  } else {
    return POST(`${CLASSROOM_ASSIGNMENTS}/update_assignment_question`, data);
  }
};

// Delete assignment question
export const deleteClassroomAssignmentQuestion = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/delete_assignment_question`, data);
};

// ==================== ASSIGNMENT SUBMISSIONS ====================

// Get assignment submissions
export const getClassroomAssignmentSubmissions = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/get_assignment_submissions`, data);
};

// Grade assignment submission
export const gradeClassroomAssignmentSubmission = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/grade_assignment`, data);
};

// Get classroom assignment details
export const getClassroomAssignmentDetails = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/get_class_assignment`, data);
};

// Get user assignment status
export const userAssignmentStatus = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/user_assignment_status`, data);
};

// Check assignment quiz user
export const checkClassroomAssignmentQuizUser = (data) => {
  return POST(`${CLASSROOM_ASSIGNMENTS}/check_assignment_quiz_user`, data);
};

// ==================== ASSIGNMENT QUIZ ====================

// Get all questions for a specific classroom assignment quiz
export const getAssignmentQuizQuestions = (data) => {
  return GET(
    `${CLASSROOM_ASSIGNMENTS}/get_quiz_questions?assignment_id=${data.assignment_id}&assignment_quiz_id=${data.assignment_quiz_id}`
  );
};

// Add a question to a classroom assignment quiz
export const addAssignmentQuizQuestion = async (data) => {
  try {
    let response;
    if (data instanceof FormData) {
      response = await UPLOAD_FILE(`${CLASSROOM_ASSIGNMENTS}/add_quiz_question`, data);
    } else {
      response = await POST(`${CLASSROOM_ASSIGNMENTS}/add_quiz_question`, data);
    }

    if (!response) {
      return {
        success: true,
        message: "Question may have been added, but no response was received",
      };
    }

    if (response.success !== undefined) {
      return response;
    }

    return {
      success: true,
      message: "Question added successfully",
      data: response,
    };
  } catch (error) {
    return { success: false, error: error.toString() };
  }
};

// Edit a question in a classroom assignment quiz
export const editAssignmentQuizQuestion = (data) => {
  if (data instanceof FormData) {
    return UPLOAD_FILE_WITH_METHOD(
      `${CLASSROOM_ASSIGNMENTS}/edit_quiz_question`,
      data,
      "PUT"
    );
  } else {
    return PUT(`${CLASSROOM_ASSIGNMENTS}/edit_quiz_question`, data);
  }
};

// Delete a question from a classroom assignment quiz
export const deleteAssignmentQuizQuestion = (data) => {
  return DELETE(`${CLASSROOM_ASSIGNMENTS}/delete_quiz_question`, data);
};

// Get detailed quiz results for a specific user in a classroom assignment quiz
export const getAssignmentQuizResultsDetailed = (params) => {
  const { assignment_id, quiz_id, user_id } = params;

  return GET(
    `${CLASSROOM_ASSIGNMENTS}/get_quiz_results_detailed?assignment_id=${assignment_id}&quiz_id=${quiz_id}&user_id=${user_id}`
  );
};
